import type { AgentWalletLedgerEntry } from '../types';

export const AGENT_WALLET_TX_TYPE_LABEL: Record<string, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer: 'Transfer',
  bonus: 'Bonus',
  adjustment: 'Adjustment',
  reservation: 'Reservation',
  reservation_release: 'Reservation release',
  credit: 'Credit',
  debit: 'Debit',
};

export function walletTypeLabel(type: string | undefined | null): string {
  if (!type) return '—';
  return AGENT_WALLET_TX_TYPE_LABEL[type] ?? type.replace(/_/g, ' ');
}

/**
 * Signed movement for the **viewed** wallet row.
 * Backend stores balance_before/after as the debited (from) wallet’s snapshot; for external credits
 * (`from_wallet` null) those are 0→0 while `amount` is correct. `direction` is resolved vs the viewer’s
 * wallet in cdnWalletLedgerBizService, so prefer direction + amount when known.
 */
export function signedChange(tx: AgentWalletLedgerEntry): number {
  const amt = tx.amount;
  const amountNum = amt != null && Number.isFinite(Number(amt)) ? Number(amt) : null;

  if (tx.direction === 'in' && amountNum != null) return Math.abs(amountNum);
  if (tx.direction === 'out' && amountNum != null) return -Math.abs(amountNum);

  const before = tx.balanceBefore;
  const after = tx.balanceAfter;
  if (before != null && after != null && Number.isFinite(Number(before)) && Number.isFinite(Number(after))) {
    return Number(after) - Number(before);
  }
  if (amountNum != null) return amountNum;
  return 0;
}

export function formatSignedAmount(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `-${formatted}`;
  return formatted;
}

/** Flow direction for the viewed wallet: IN (credit) vs OUT (debit). */
export function walletInOutLabel(tx: AgentWalletLedgerEntry): 'IN' | 'OUT' | '—' {
  const delta = signedChange(tx);
  if (delta > 0) return 'IN';
  if (delta < 0) return 'OUT';
  return '—';
}

/** Reference / external id for display. */
export function referenceLabel(tx: AgentWalletLedgerEntry): string {
  const rid = tx.referenceId?.trim();
  if (rid) return rid;
  const tid = tx.transactionId?.trim();
  if (tid) return tid;
  return '—';
}

/** Ledger row copy: prefer API `description`, else reference ids. */
export function ledgerDescriptionDisplay(tx: AgentWalletLedgerEntry): string {
  const d = tx.description != null ? String(tx.description).trim() : '';
  if (d) return d;
  return referenceLabel(tx);
}

/** When the other side of the movement is a shop (e.g. distribution / transfer). */
export function distributionTarget(tx: AgentWalletLedgerEntry): { shopId: string; shopName: string } | null {
  const cp = tx.counterparty;
  if (cp?.ownerType === 'shop' && cp.ownerId) {
    return { shopId: cp.ownerId, shopName: 'Shop' };
  }
  return null;
}

export function balanceBeforeNum(tx: AgentWalletLedgerEntry): number {
  const v = tx.balanceBefore;
  if (v == null) return NaN;
  return Number(v);
}
